import {type CSSProperties, useCallback} from 'react';
import amplitudeService from '@features/analytics/amplitudeService';
import {trpc} from '@features/utils/trpc';
import {
  useGeneratingScreenname,
  useIsScreenGenerating,
} from '@features/ai/streaming/useGeneration';
import {
  isComponentLibraryItem,
  isComponentScreen,
} from '@features/flow/utils/componentScreenId';
import {InlineEditableText} from './InlineEditableText';

export const EditableScreenName = ({
  chatId,
  isEditable,
  setIsEditable,
  screenCount,
  className,
  style,
}: {
  chatId: string;
  isEditable: boolean;
  setIsEditable: (isEditable: boolean) => void;
  screenCount: number;
  className?: string;
  style?: CSSProperties;
}) => {
  const newName = useGeneratingScreenname(chatId);
  const isGenerating = useIsScreenGenerating(chatId);
  const {data: chat} = trpc.screens.getScreen.useQuery(
    {screenId: chatId},
    {enabled: chatId !== '', staleTime: 60_000, refetchOnMount: false},
  );
  const screenName = newName ?? chat?.screenName;
  const screenNameDisplayed =
    screenName ?? (isGenerating ? 'Designing…' : `Screen ${screenCount + 1}`);

  const trpcUtils = trpc.useUtils();
  const updateScreen = trpc.screens.updateScreen.useMutation({
    onSuccess: async (_data, {screenId}) => {
      await trpcUtils.screens.getScreen.invalidate({screenId});
    },
  });
  const updateComponentScreenMutation =
    trpc.screens.updateComponentScreen.useMutation({
      onSuccess: async (_data, {screenId}) => {
        await trpcUtils.screens.getScreen.invalidate({screenId});
      },
    });

  const handleSave = useCallback(
    (newValue: string) => {
      amplitudeService.logEvent('Screen name change');
      if (isComponentScreen(chatId)) {
        updateComponentScreenMutation.mutate({
          screenId: chatId,
          displayName: newValue,
        });
      } else {
        updateScreen.mutate({
          screenId: chatId,
          updates: {screenName: newValue},
        });
      }
    },
    [chatId, updateScreen, updateComponentScreenMutation],
  );

  const isLibItem = isComponentLibraryItem(chatId);

  return (
    <InlineEditableText
      value={screenNameDisplayed}
      isEditable={isLibItem ? false : isEditable}
      setIsEditable={isLibItem ? () => {} : setIsEditable}
      onSave={handleSave}
      className={className}
      style={style}
    />
  );
};
